/**
 * Nutrition Logs Hook
 * CRUD operations for nutrition log entries
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { NutritionLog, NutritionLogInput, MealType } from '@/lib/nutrition';
import { format, parseISO, subDays } from 'date-fns';

interface UseNutritionLogsOptions {
  clientId: string;
  dateRange?: { from: Date; to: Date };
  mealTypes?: MealType[];
  status?: string[];
}

export function useNutritionLogs({
  clientId,
  dateRange,
  mealTypes,
  status,
}: UseNutritionLogsOptions) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['nutrition-logs', clientId, dateRange, mealTypes, status];

  const logsQuery = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('nutrition_logs')
        .select('*')
        .eq('client_id', clientId)
        .order('logged_date', { ascending: false })
        .order('logged_at', { ascending: false });

      if (dateRange) {
        query = query
          .gte('logged_date', format(dateRange.from, 'yyyy-MM-dd'))
          .lte('logged_date', format(dateRange.to, 'yyyy-MM-dd'));
      }

      if (mealTypes && mealTypes.length > 0) {
        query = query.in('meal_type', mealTypes);
      }

      if (status && status.length > 0) {
        query = query.in('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as NutritionLog[];
    },
    enabled: !!clientId,
    staleTime: 30000, // 30 seconds - real-time handles updates
  });

  // Real-time subscription - trainers see client nutrition updates live
  useEffect(() => {
    if (!clientId) return;

    const channel = supabase
      .channel(`nutrition_logs_${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'nutrition_logs',
          filter: `client_id=eq.${clientId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['nutrition-logs', clientId] });
          queryClient.invalidateQueries({ queryKey: ['nutrition-summary', clientId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId, queryClient]);

  const createLog = useMutation({
    mutationFn: async (input: NutritionLogInput) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('nutrition_logs')
        .insert({
          client_id: clientId,
          meal_type: input.meal_type,
          food_description: input.food_description || null,
          calories: input.calories ?? null,
          protein_g: input.protein_g ?? null,
          carbs_g: input.carbs_g ?? null,
          fat_g: input.fat_g ?? null,
          fiber_g: input.fiber_g ?? null,
          water_oz: input.water_oz ?? null,
          client_notes: input.client_notes || null,
          logged_date: input.logged_date || format(new Date(), 'yyyy-MM-dd'),
          submitted_by: session.session.user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data as NutritionLog;
    },
    onSuccess: () => {
      // Real-time subscription handles invalidation
      toast({
        title: 'Entry logged',
        description: 'Your nutrition entry has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateLog = useMutation({
    mutationFn: async ({ id, ...input }: Partial<NutritionLogInput> & { id: string }) => {
      const updateData: Record<string, unknown> = {};

      if (input.meal_type !== undefined) updateData.meal_type = input.meal_type;
      if (input.food_description !== undefined) updateData.food_description = input.food_description;
      if (input.calories !== undefined) updateData.calories = input.calories;
      if (input.protein_g !== undefined) updateData.protein_g = input.protein_g;
      if (input.carbs_g !== undefined) updateData.carbs_g = input.carbs_g;
      if (input.fat_g !== undefined) updateData.fat_g = input.fat_g;
      if (input.fiber_g !== undefined) updateData.fiber_g = input.fiber_g;
      if (input.water_oz !== undefined) updateData.water_oz = input.water_oz;
      if (input.client_notes !== undefined) updateData.client_notes = input.client_notes;

      // If edited by client, reset to pending for re-review
      updateData.status = 'pending';

      const { data, error } = await supabase
        .from('nutrition_logs')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as NutritionLog;
    },
    onSuccess: () => {
      // Real-time subscription handles invalidation
      toast({
        title: 'Entry updated',
        description: 'Your changes have been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteLog = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('nutrition_logs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Real-time subscription handles invalidation
      toast({
        title: 'Entry deleted',
        description: 'The nutrition entry has been removed.',
        duration: 6000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const copyLog = useMutation({
    mutationFn: async ({ logId, targetDate }: { logId: string; targetDate?: string }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      // Get the original log
      const { data: original, error: fetchError } = await supabase
        .from('nutrition_logs')
        .select('*')
        .eq('id', logId)
        .single();

      if (fetchError || !original) throw fetchError || new Error('Log not found');

      // Create a copy
      const { data, error } = await supabase
        .from('nutrition_logs')
        .insert({
          client_id: original.client_id,
          meal_type: original.meal_type,
          food_description: original.food_description,
          calories: original.calories,
          protein_g: original.protein_g,
          carbs_g: original.carbs_g,
          fat_g: original.fat_g,
          fiber_g: original.fiber_g,
          water_oz: original.water_oz,
          client_notes: original.client_notes,
          logged_date: targetDate || format(new Date(), 'yyyy-MM-dd'),
          submitted_by: session.session.user.id,
          copied_from_id: logId,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data as NutritionLog;
    },
    onSuccess: () => {
      // Real-time subscription handles invalidation
      toast({
        title: 'Entry copied',
        description: 'The meal has been copied to today.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    logs: logsQuery.data || [],
    isLoading: logsQuery.isLoading,
    error: logsQuery.error,
    createLog,
    updateLog,
    deleteLog,
    copyLog,
    refetch: logsQuery.refetch,
  };
}

// Hook for quick water logging
export function useQuickWaterLog(clientId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (waterOz: number) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('nutrition_logs')
        .insert({
          client_id: clientId,
          meal_type: 'water',
          water_oz: waterOz,
          logged_date: format(new Date(), 'yyyy-MM-dd'),
          submitted_by: session.session.user.id,
          status: 'approved', // Water is auto-approved
        })
        .select()
        .single();

      if (error) throw error;
      return data as NutritionLog;
    },
    onSuccess: () => {
      // Real-time subscription handles invalidation
      toast({
        title: 'Water logged',
        description: 'Stay hydrated!',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to get logs for a specific day
export function useDayLogs(clientId: string, date: Date) {
  const dateStr = format(date, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['nutrition-logs', clientId, 'day', dateStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nutrition_logs')
        .select('*')
        .eq('client_id', clientId)
        .eq('logged_date', dateStr)
        .order('logged_at', { ascending: true });

      if (error) throw error;
      return (data || []) as NutritionLog[];
    },
    enabled: !!clientId,
    staleTime: 30000, // 30 seconds
  });
}
